
// Local IndexDB datastore helpers
const ENTITY_MODELS = [ 'Facilities', 'Floors', 'Spaces', 'Assets' ]
const UNSYNCED_MODEL = 'Unsynced'
const ENTITY_STORE_NAME = {
  Assets: 'user_Asset',
  Facilities: 'user_Facility',
  Floors: 'user_Floor',
  Spaces: 'user_Space',
  Unsynced: 'sync_MutationEvent'
}

let indexDb = undefined
let tenantName = undefined

function generateDownload (data, type) {
  const date = new Date()

  Object.assign(document.createElement('a'), {
    download: `ELIAS ${type} - ${tenantName} - ${date}.json`,
    href: URL.createObjectURL(new Blob([data]), {
      type: 'application/json'
    })
  }).dispatchEvent(new MouseEvent('click'))
}

function deleteDequeuedAndErrors () {
  const db = indexDb?.result
  const objStoreName = 'keyval'
  const transaction = db.transaction(objStoreName, 'readwrite')
  const store = transaction.objectStore(objStoreName)
  const queryTable = store.openCursor()

  queryTable.onsuccess = function () {
    const elementsToDelete = [ 'error', 'dequeued' ]
    const cursor = queryTable?.result

    if (cursor) {
      if (elementsToDelete.some(e => cursor.primaryKey.startsWith(e))) {
        const request = cursor.delete()

        request.onsuccess = () => console.info(`Successfully deleted: ${cursor.primaryKey}`)
      }

      cursor.continue()
    } else {
      console.error('An error occured when attempting to clear the dequeued items. No entries deleted')
    }

    db.close()
  }
}

const getDequeuedRecords = () => new Promise((resolve, reject) => {
  indexDb = window.indexedDB.open('keyval-store', 1)

  indexDb.onsuccess = function (event) {
    const db = event?.target?.result
    const objStoreName = 'keyval'
    const transaction = db.transaction(objStoreName, 'readonly')
    const store = transaction.objectStore(objStoreName)
    const queryTable = store.openCursor(IDBKeyRange.bound('dequeued', 'dequeued\uffff'))
    const records = []

    queryTable.onsuccess = function () {
      const cursor = queryTable.result
      
      if (cursor) {
        const obj = { [cursor.primaryKey]: cursor.value }

        records.push(obj)

        cursor.continue()
      } else {
        // No more entries to iterate over
        console.info("all dequeued data =", records, "\nsize =", records.length)

        resolve(records)
      }
    }
  }

  indexDb.onerror = function () {
    reject(Error('An error occured when trying to access the indexedDB database - getDequeuedRecords'))
  }

  indexDb.onupgradeneeded = function (event) {
    event?.target?.transaction?.abort()

    reject(Error('ObjectStore does not exist'))
  }
})

const getEntityData = model => new Promise((resolve, reject) => {
  indexDb = window.indexedDB.open('amplify-datastore', 2)

  const objStoreName = model === UNSYNCED_MODEL ? ENTITY_STORE_NAME.Unsynced : ENTITY_STORE_NAME[model]

  indexDb.onsuccess = function (event) {
    const db = event?.target?.result
    const transaction = db.transaction(objStoreName, 'readonly')
    const store = transaction.objectStore(objStoreName)
    const queryTable = store.openCursor()
    const records = []

    queryTable.onsuccess = function () {
      const cursor = queryTable.result

      if (cursor) {
        const obj = { [cursor.primaryKey]: cursor.value }
        
        records.push(obj)
        
        cursor.continue()
      } else {
        // No more entries to iterate over
        console.info(`Exporting ${records.length} records for ${model}`)

        db.close()

        resolve(records)
      }
    }
  }

  indexDb.onerror = function () {
    reject(Error('An error occured when trying to access the indexedDB database - getEntityData'))
  }

  indexDb.onupgradeneeded = function (event) {
    event?.target?.transaction?.abort()

    reject(Error(`${objStoreName} in indexedDB does not exist - getEntityData`))
  }
})

const getPendingImages = () => new Promise((resolve, reject) => {
  indexDb = window.indexedDB.open('keyval-store', 1)

  const objStoreName = 'keyval'

  indexDb.onsuccess = function (event) {
    const db = event?.target?.result
    let records = []

    const request = db.transaction(objStoreName, 'readonly')
      .objectStore(objStoreName)
      .openCursor(IDBKeyRange.bound('pendingImage', 'pendingImage\uffff'))
    
    request.onsuccess = () => {
      const cursor = request?.result

      if (cursor) {
        const obj = { [cursor.primaryKey ]: cursor.value }

        records.push(obj)

        cursor.continue()
      } else {
        // No more entries to iterate over
        db.close()

        resolve(records)
      }
    }
  }

  indexDb.onerror = function () {
    reject(Error('An error occured when trying to access the indexedDb database - getPendingImages'))
  }

  indexDb.onupgradeneeded = function (event) {
    event?.target?.transaction?.abort()

    reject(Error(`${objStoreName} in indexedDB does not exist - getPendingImages`))
  }
})

export function exportDataIssues (tenant) {
  tenantName = tenant

  const pendingReq = getDequeuedRecords()

  Promise.resolve(pendingReq)
    .then(values => {
      const downloadData = JSON.stringify(values, null, 2)

      deleteDequeuedAndErrors()
      generateDownload(downloadData, 'Export Issues Report')
    }
  )
}

export async function exportUnsyncedData (tenant) {
  tenantName = tenant

  const pendingUpdates = await getEntityData(UNSYNCED_MODEL)
  const pendingImages = await getPendingImages()
  const imagesArray = []

  pendingImages.length && pendingImages.forEach(img => {
    const imageKey = Object.keys(img)[0].replace('pendingImage-', '')
    const hash = imageKey.split('-')[imageKey.split('-').length - 1]
    const image = Object.values(img)[0]

    const imgObj = {
      hash,
      image
    }

    imagesArray.push(imgObj)
  })

  const downloadData = JSON.stringify({
    pending: pendingUpdates,
    images: imagesArray
  }, null, 2)

  generateDownload(downloadData, 'Export Unsynced Data')
}

export function exportDeviceData (tenant) {
  tenantName = tenant

  const deviceData = {}

  ENTITY_MODELS.forEach(m => {
    deviceData[m] = getEntityData(m)
  })

  // Note: Do not change the order of the Promise iterable array without updating the dataStoreData object indexes accordingly
  Promise.all([deviceData.Assets, deviceData.Facilities, deviceData.Floors, deviceData.Spaces])
    .then(values => {
      const aggregatedDeviceData = {
        Assets: values[0],
        Facilities: values[1],
        Floors: values[2],
        Spaces: values[3]
      }

      const downloadData = JSON.stringify(aggregatedDeviceData, null, 2)
      
      generateDownload(downloadData, 'Export All Data')    
  })
}
